import React, { useState, useEffect } from 'react';
import { useSecretaryStore } from '../store/secretaryStore';
import { X, Calendar, Clock, User, AlertCircle, Edit2, Trash2, RefreshCcw, Save } from 'lucide-react';
import { updateEstadoCita, reagendarCitaLogistica } from '../services/CalendarService';
export function VistaCitaSlideOver() {
  const { 
    citaCalendarioSeleccionada, 
    seleccionarCitaCalendario, 
    iniciarLimbo, 
    setIsLoadingCalendario,
    setCheckoutCitaOpen,
    citasCalendario,
    setCitasCalendario,
    forceRefreshCalendario,
    addNotificacion,
  } = useSecretaryStore();

  const [isRescheduling, setIsRescheduling] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');

  useEffect(() => {
    setIsRescheduling(false);
    if (citaCalendarioSeleccionada) {
      const start = new Date(citaCalendarioSeleccionada.fecha_hora_inicio);
      const tzOffset = start.getTimezoneOffset() * 60000; 
      const localISOTime = (new Date(start.getTime() - tzOffset)).toISOString().slice(0, -1);
      
      setNewDate(localISOTime.split('T')[0]);
      setNewStartTime(localISOTime.split('T')[1].substring(0, 5));
    }
  }, [citaCalendarioSeleccionada]);

  if (!citaCalendarioSeleccionada) return null;

  const cita = citaCalendarioSeleccionada;
  const isManual = cita.tipo === 'cita_manual' || cita.tipo === 'ocupado';

  // Formateo de fecha y hora
  const fechaObj = new Date(cita.fecha_hora_inicio);
  const fechaTexto = fechaObj.toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const horaInicio = fechaObj.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: true });
  
  const finObj = new Date(cita.fecha_hora_fin);
  const horaFin = finObj.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: true });

  const handleClose = () => {
    seleccionarCitaCalendario(null);
  };

  const handleReagendar = () => {
    setIsRescheduling(true);
  };

  const handleGuardarReagendar = async () => {
    if (!newDate || !newStartTime) return;
    
    // Calculate new end time based on original duration
    const origStart = new Date(cita.fecha_hora_inicio).getTime();
    const origEnd = new Date(cita.fecha_hora_fin).getTime();
    const durationMs = origEnd - origStart;

    const [yyyy, mm, dd] = newDate.split('-');
    const [hh, min] = newStartTime.split(':');
    const newStartObj = new Date(Number.parseInt(yyyy), Number.parseInt(mm) - 1, Number.parseInt(dd), Number.parseInt(hh), Number.parseInt(min));
    const newEndObj = new Date(newStartObj.getTime() + durationMs);

    const nuevaFechaInicioIso = newStartObj.toISOString();
    const nuevaFechaFinIso = newEndObj.toISOString();

    // Workaround for Chromium bug: blur active input to destroy native popup before unmounting
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    handleClose(); // Close modal immediately for UX

    const snapshotCitas = [...citasCalendario];
    const nuevasCitas = citasCalendario.map(c => 
      c.cita_id === cita.cita_id 
        ? { ...c, fecha_hora_inicio: nuevaFechaInicioIso, fecha_hora_fin: nuevaFechaFinIso }
        : c
    );
    setCitasCalendario(nuevasCitas); // OPTIMISTIC UPDATE

    iniciarLimbo(
      `Reagendando cita a ${newDate} ${newStartTime}`,
      async () => {
        setIsLoadingCalendario(true);
        const { success, message } = await reagendarCitaLogistica(
          cita.cita_id,
          nuevaFechaInicioIso,
          nuevaFechaFinIso,
          cita.profesional_uuid,
          cita.tipo === 'cita_manual' || cita.tipo === 'ocupado'
        );
        
        if (!success) {
          addNotificacion(`Error al reagendar la cita: ${message}`, 'warning');
        }
        
        forceRefreshCalendario();
        setIsLoadingCalendario(false);
      },
      () => {
        setCitasCalendario(snapshotCitas);
      }
    );
  };

  const handleCancelar = () => {
    handleClose(); // Cerramos el panel inmediatamente por UX

    const snapshotCitas = [...citasCalendario];
    
    // OPTIMISTIC UPDATE: Removemos la cita visualmente
    if (isManual) {
      setCitasCalendario(citasCalendario.filter(c => c.cita_id !== cita.cita_id));
    } else {
      setCitasCalendario(citasCalendario.map(c => 
        c.cita_id === cita.cita_id ? { ...c, estado: 'cancelada_paciente' } : c
      ));
    }

    iniciarLimbo(
      cita.tipo === 'ocupado' ? 'Eliminando bloqueo de horario' : `Cancelando cita de ${cita.paciente_nombre || 'Paciente'}`,
      async () => {
        setIsLoadingCalendario(true);
        const { success, message } = await updateEstadoCita(cita.cita_id, 'cancelada_paciente', isManual);
        
        if (!success) {
          addNotificacion(`Error al cancelar la cita: ${message}`, 'warning');
        }
        
        forceRefreshCalendario();
        setIsLoadingCalendario(false);
      },
      () => {
        // Al deshacer, restauramos el snapshot
        setCitasCalendario(snapshotCitas);
      }
    );
  };

  const handleRestaurar = () => {
    handleClose(); // Cerramos el panel

    iniciarLimbo(
      `Restaurando cita de ${cita.paciente_nombre}`,
      async () => {
        setIsLoadingCalendario(true);
        // La regresamos a 'confirmada' o 'reservada_ttl' por defecto
        const { success, message } = await updateEstadoCita(cita.cita_id, 'confirmada', isManual);
        
        if (!success) {
          addNotificacion(`Error al restaurar la cita: ${message}`, 'warning');
        }
        
        setIsLoadingCalendario(false);
      },
      () => {}
    );
  };

  const handleUpdateStatus = (newStatus: string) => {
    handleClose(); // Close the panel
    
    let actionText = `Estado cambiado a ${newStatus}`;
    if (newStatus === 'completada') actionText = `Cita de ${cita.paciente_nombre} completada`;
    if (newStatus === 'no_show_paciente') actionText = `Paciente ${cita.paciente_nombre} ausente`;
    if (newStatus === 'confirmada') actionText = `Restaurando cita de ${cita.paciente_nombre} a confirmada`;
    
    iniciarLimbo(
      actionText,
      async () => {
        setIsLoadingCalendario(true);
        const { success, message } = await updateEstadoCita(cita.cita_id, newStatus, isManual);
        
        if (!success) {
          addNotificacion(`Error al actualizar estado: ${message}`, 'warning');
        }
        setIsLoadingCalendario(false);
      },
      () => {}
    );
  };

  const isCancelada = cita.estado === 'cancelada_paciente';

  const renderFooter = () => {
    if (isCancelada) {
      return (
        <button
          onClick={handleRestaurar}
          className="w-full py-2.5 font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 active:bg-emerald-200 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-sm"
        >
          <RefreshCcw className="h-4 w-4" />
          Restaurar Cita Cancelada
        </button>
      );
    }

    if (isRescheduling) {
      return (
        <div className="flex items-center gap-3 w-full shrink-0">
          <button
            type="button"
            className="flex-1 py-3 px-4 font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all flex items-center justify-center gap-2"
            onClick={() => setIsRescheduling(false)}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="flex-2 py-3 px-4 font-bold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all flex items-center justify-center gap-2 shadow-sm"
            onClick={handleGuardarReagendar}
          >
            <Save className="h-4 w-4" />
            Guardar
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 w-full shrink-0">
        {/* Acciones Rápidas (Conclusión) */}
        {cita.estado === 'confirmada' && (
          <div className="flex gap-2 w-full">
            <button
              type="button"
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors shadow-sm"
              onClick={() => handleUpdateStatus('completada')}
            >
              ATENDIDA
            </button>
            <button
              type="button"
              className="flex-1 bg-white hover:bg-rose-50 text-slate-500 hover:text-rose-600 border border-slate-200 hover:border-rose-200 font-bold text-sm px-4 py-2.5 rounded-lg transition-colors"
              onClick={() => handleUpdateStatus('no_show_paciente')}
            >
              AUSENTE
            </button>
          </div>
        )}
        
        {(cita.estado === 'completada' || cita.estado === 'no_show_paciente') && (
          <button
            type="button"
            className="w-full bg-white hover:bg-slate-100 text-slate-600 border border-slate-200 font-bold text-sm px-4 py-2.5 rounded-lg transition-colors shadow-sm"
            onClick={() => handleUpdateStatus('confirmada')}
          >
            <RefreshCcw className="h-4 w-4 inline-block mr-2" />
            DESHACER (Volver a Confirmada)
          </button>
        )}

        {/* Acciones de Gestión */}
        <div className="flex flex-wrap sm:grid sm:grid-cols-3 gap-2 w-full pt-1 [&>button]:flex-1 [&>button]:min-w-25">
          <button
            type="button"
            className={`flex flex-col items-center justify-center gap-1 font-medium text-xs py-2 rounded-lg transition-colors ${
              cita.tipo === 'cita_manual'
                ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
            onClick={() => {
              if (cita.tipo !== 'cita_manual') {
                setCheckoutCitaOpen(true);
              }
            }}
            title={cita.tipo === 'cita_manual' ? 'El Checkout (CRM/Cobros) solo está disponible para citas con perfiles registrados' : 'Abrir Checkout'}
          >
            <Edit2 className="h-4 w-4" />
            Checkout
          </button>
          <button
            type="button"
            className="flex-1 min-w-25 flex flex-col items-center justify-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium text-xs py-2 rounded-lg transition-colors"
            onClick={handleReagendar}
          >
            <Calendar className="h-4 w-4" />
            Reagendar
          </button>
          <button
            type="button"
            className="flex-1 min-w-22.5 flex flex-col items-center justify-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 font-medium text-xs py-2 rounded-lg transition-colors"
            onClick={handleCancelar}
          >
            <Trash2 className="h-4 w-4" />
            Cancelar
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <button 
        type="button"
        className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-100 animate-in fade-in duration-200 cursor-default"
        onClick={handleClose}
        aria-label="Cerrar modal"
      />

      {/* Slide-over Panel */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-100 bg-white dark:bg-slate-900 shadow-2xl z-101 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200 dark:border-slate-800">
        
        {/* Cabecera */}
        <div 
          className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 z-10 relative"
          style={{ padding: '16px 20px' }}
        >
          <h2 className="text-lg font-black text-slate-800 dark:text-slate-100 flex items-center gap-2.5 tracking-tight">
            <div className="rounded-lg" style={{ backgroundColor: '#eff6ff', color: '#2563eb', padding: '8px' }}>
              <Calendar className="h-5 w-5" />
            </div>
            Detalles de la Cita
          </h2>
          <button 
            onClick={handleClose}
            className="p-2.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Contenido (Solo Lectura) */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div 
          className="flex-1 overflow-y-auto flex flex-col gap-4"
          style={{ padding: '16px 20px' }}
          onClick={(e) => {
            if (e.target === e.currentTarget && document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }}
        >

          {/* Paciente */}
          <div 
            className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col"
            style={{ padding: '16px', gap: '8px' }}
          >
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center" style={{ gap: '6px' }}>
              <User className="h-4 w-4" />
              Paciente
            </div>
            <div className="text-lg font-bold text-slate-800 dark:text-slate-100">
              {cita.paciente_nombre}
            </div>
            {cita.tipo === 'cita_manual' && (
              <span className="inline-flex items-center w-fit px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 mt-1">
                Paciente Manual (Sin Registro)
              </span>
            )}
          </div>

          {/* Fecha y Hora */}
          <div 
            className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col"
            style={{ padding: '16px', gap: '14px' }}
          >
            <div className="flex flex-col" style={{ gap: '6px' }}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center" style={{ gap: '6px' }}>
                <Calendar className="h-4 w-4" />
                Fecha
              </div>
              
              {isRescheduling ? (
                <div className="mt-1">
                  <input 
                    type="date" 
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow cursor-pointer"
                  />
                </div>
              ) : (
                <div className="text-base font-semibold text-slate-700 dark:text-slate-300 capitalize">
                  {fechaTexto}
                </div>
              )}
            </div>

            <div className="w-full h-px bg-slate-200 dark:bg-slate-700" />

            <div className="flex flex-col" style={{ gap: '6px' }}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center" style={{ gap: '6px' }}>
                <Clock className="h-4 w-4" />
                Horario
              </div>
              
              {isRescheduling ? (
                <div className="mt-1">
                  <input 
                    type="time" 
                    value={newStartTime}
                    onChange={(e) => setNewStartTime(e.target.value)}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow cursor-pointer"
                  />
                </div>
              ) : (
                <div className="text-base font-semibold text-slate-700 dark:text-slate-300">
                  {horaInicio} - {horaFin}
                </div>
              )}
            </div>
          </div>

          {/* Profesional y Servicio */}
          <div 
            className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col"
            style={{ padding: '16px', gap: '14px' }}
          >
            <div className="flex flex-col" style={{ gap: '6px' }}>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center" style={{ gap: '6px' }}>
                <User className="h-4 w-4" />
                Profesional
              </div>
              <div className="text-base font-semibold text-slate-700 dark:text-slate-300">
                Dr. {cita.profesional_nombre}
              </div>
            </div>

            {cita.servicio_titulo && (
              <>
                <div className="w-full h-px bg-slate-200 dark:bg-slate-700" />
                <div className="flex flex-col gap-1.5">
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Servicio
                  </div>
                  <div className="text-base font-semibold text-slate-700 dark:text-slate-300">
                    {cita.servicio_titulo}
                  </div>
                </div>
              </>
            )}
          </div>

          {cita.nota && (
            <div 
              className="bg-blue-50/50 rounded-2xl border border-blue-100 flex flex-col"
              style={{ padding: '16px', gap: '8px' }}
            >
              <div className="text-xs font-bold text-blue-600 uppercase tracking-wider flex items-center" style={{ gap: '6px' }}>
                <AlertCircle className="h-4 w-4" />
                Nota Adicional
              </div>
              <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                {cita.nota}
              </div>
            </div>
          )}
        </div>

        {/* Botones de Acción (Footer) */}
        <div 
          className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)] z-10 relative"
          style={{ padding: '16px 20px' }}
        >
          {renderFooter()}
        </div>
      </div>
    </>
  );
}
